import {
  OwnerType,
  TForbidenCountryIndividual,
  TOwnerDiagramNode,
  TOwnerLink,
} from '~/modules/identification/forms/OwnershipStructure/types.ts';
import { Edge, Node, Position } from 'reactflow';
import {
  BASE_HEIGHT,
  edgeStyle,
  HORIZONTAL,
  individualTypes,
  LINE_HEIGHT,
  NODE_HEIGHT,
  NODE_WIDTH,
  ROOT_NODE_ID,
} from '../constants';

export const getOwnerName = (owner: TOwnerDiagramNode) => {
  const personName = [owner.lastName, owner.firstName, owner.middleName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return personName || owner.fullName || owner.shortNameUkr || '';
};

export const getTodayDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}.${month}.${year}`;
};

export const calculateNodeHeight = (owner: TOwnerDiagramNode) => {
  let rows = 0;

  switch (owner.type) {
    case OwnerType.INDIVIDUAL:
      rows += 1;
      if (owner.birthDate) rows++;
      if (owner.identificationNumber || owner.foreignTaxNumber) rows++;
      if (owner.citizenshipCountry) rows++;
      if (owner.address?.permanentAddress?.country) rows++;
      if (owner.isBeneficiary) rows++;
      break;
    case OwnerType.LEGAL_ENTITY:
      rows += 1;
      if (owner.identificationNumber || owner.foreignTaxNumber) rows++;
      if (owner.address?.registrationAddress?.address) {
        rows += Math.ceil(owner.address.registrationAddress.address.length / 40);
      }
      break;
    case OwnerType.FREE_FLOAT:
      rows += 1;
      if (owner.sourceURL) {
        rows += Math.ceil(owner.sourceURL.length / 40);
      }
      break;
    case OwnerType.SHAREHOLDER_GROUP:
      rows += 1;
      if (owner.rangeLegal) rows++;
      if (owner.rangeIndividual) rows++;
      break;
    case OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP:
      rows += 1;
      if (owner.birthDate) rows++;
      if (owner.identificationNumber || owner.foreignTaxNumber) rows++;
      if (owner.citizenshipCountry) rows++;
      if (owner.address?.permanentAddress?.country) rows++;
      if (owner.isBeneficiary) rows++;
      break;
  }
  return BASE_HEIGHT + rows * LINE_HEIGHT;
};

export const collectLevels = (owners: TOwnerDiagramNode[]): Map<string, number> => {
  const levelMap = new Map<string, number>();

  const traverse = (owner: TOwnerDiagramNode) => {
    if (owner.type === OwnerType.LEGAL_ENTITY) {
      const current = levelMap.get(owner.id);
      if (current === undefined || owner.level < current) {
        levelMap.set(owner.id, owner.level);
      }
    }
    owner.children?.forEach(traverse);
  };

  owners.forEach(traverse);
  return levelMap;
};

export const buildFlowElements = (
  tree: TOwnerDiagramNode[],
  rootFullName: string,
  identificationNumber: string,
  links: TOwnerLink[]
): [Node[], Edge[]] => {
  const nodes: Node[] = [
    {
      id: ROOT_NODE_ID,
      type: 'default',
      position: { x: 0, y: 0 },
      data: {
        ownerType: OwnerType.LEGAL_ENTITY,
        fullName: rootFullName,
        identificationNumber: identificationNumber,
        isRoot: true,
      },
    },
  ];

  const edges: Edge[] = [];
  const seenOwnerIds = new Set<string>();
  const duplicateCounters = new Map<string, number>();
  const levelMap = collectLevels(tree);

  const linkMap = new Map<string, number>();
  links.forEach((link) => {
    linkMap.set(`${link.fromNodeId}->${link.toNodeId}`, +(link.ownershipPercentage || 0));
  });

  const getOwnershipPercentage = (owner: TOwnerDiagramNode, parentId: string): number => {
    const cleanParentId = parentId.split('__dup')[0];
    const linkParentId = cleanParentId === ROOT_NODE_ID ? 'MainLegal' : cleanParentId;
    return linkMap.get(`${owner.id}->${linkParentId}`) ?? 0;
  };

  const pushNode = (
    nodeId: string,
    owner: TOwnerDiagramNode,
    isDuplicate: boolean,
    ownershipPercentage: number
  ) => {
    nodes.push({
      id: nodeId,
      type: 'default',
      position: { x: 0, y: 0 },
      data: {
        sourceOwnerId: owner.id,
        ownerType: owner.type,
        level: owner.level,
        height: calculateNodeHeight(owner),
        fullName: getOwnerName(owner),
        birthDate: owner.birthDate || null,
        identificationNumber: owner.identificationNumber || null,
        foreignTaxNumber: owner.foreignTaxNumber || null,
        citizenshipCountry: owner.citizenshipCountry || null,
        ownershipPercentage,
        isBeneficiary: owner?.isBeneficiary ?? false,
        shortNameUkr: owner.shortNameUkr || null,
        address: owner.address || null,
        registrationAddress: owner.address?.registrationAddress?.address || null,
        sourceURL: owner.sourceURL || null,
        rangeIndividual: owner.rangeIndividual || null,
        rangeLegal: owner.rangeLegal || null,
        hasRussianOrBelarusianCitizenShareholders:
          owner.hasRussianOrBelarusianCitizenShareholders || null,
        russianBelarusianIndividuals: owner.russianBelarusianIndividuals || null,
        russianBelarusianLegalEntities: owner.russianBelarusianLegalEntities || null,
        areIndividualsWithDecisiveInfluenceAbsent:
          owner.areIndividualsWithDecisiveInfluenceAbsent || null,
        isDuplicate,
      },
    });
  };

  const nextNodeId = (ownerId: string): { nodeId: string; isFirstOccurrence: boolean } => {
    const count = (duplicateCounters.get(ownerId) ?? 0) + 1;
    duplicateCounters.set(ownerId, count);
    return {
      nodeId: count === 1 ? ownerId : `${ownerId}__dup${count - 1}`,
      isFirstOccurrence: count === 1,
    };
  };

  const walk = (owner: TOwnerDiagramNode, parentId: string) => {
    const ownershipPercentage = getOwnershipPercentage(owner, parentId);

    if (owner.type === OwnerType.LEGAL_ENTITY) {
      const minLevel = levelMap.get(owner.id);
      const isCanonicalLevel = owner.level === minLevel;
      const alreadyTakenCanonical = seenOwnerIds.has(owner.id);

      if (isCanonicalLevel && !alreadyTakenCanonical) {
        seenOwnerIds.add(owner.id);
        pushNode(owner.id, owner, false, ownershipPercentage);
        edges.push({
          id: `${owner.id}-${parentId}`,
          source: owner.id,
          target: parentId,
          ...edgeStyle,
        });

        owner.children?.forEach((child) => walk(child, owner.id));
      } else {
        const count = (duplicateCounters.get(owner.id) ?? 0) + 1;
        duplicateCounters.set(owner.id, count);
        const duplicateNodeId = `${owner.id}__dup${count}`;
        pushNode(duplicateNodeId, owner, true, ownershipPercentage);
        edges.push({
          id: `${duplicateNodeId}-${parentId}`,
          source: duplicateNodeId,
          target: parentId,
          ...edgeStyle,
        });
      }
    } else {
      const { nodeId } = nextNodeId(owner.id);

      pushNode(nodeId, owner, false, ownershipPercentage);
      edges.push({
        id: `${nodeId}-${parentId}`,
        source: nodeId,
        target: parentId,
        ...edgeStyle,
      });

      owner.children?.forEach((child) => walk(child, nodeId));
    }
  };

  tree.forEach((owner) => walk(owner, ROOT_NODE_ID));

  return [nodes, edges];
};

export function getLayoutedElements(nodes: Node[], edges: Edge[]): [Node[], Edge[]] {
  const childrenMap = new Map<string, string[]>();

  edges.forEach((edge) => {
    const parentId = edge.target;
    const childId = edge.source;

    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }

    childrenMap.get(parentId)?.push(childId);
  });

  const positionedNodes = new Map<string, { x: number; level: number }>();
  let currentLeafIndex = 0;

  const calculatePositions = (nodeId: string, level: number): number => {
    const children = childrenMap.get(nodeId) || [];

    if (children.length === 0) {
      const x = currentLeafIndex * HORIZONTAL;

      positionedNodes.set(nodeId, { x, level });
      currentLeafIndex++;
      return x;
    }

    const childXs = children.map((childId) => calculatePositions(childId, level + 1));
    const minX = Math.min(...childXs);
    const maxX = Math.max(...childXs);
    const x = (minX + maxX) / 2;

    positionedNodes.set(nodeId, { x, level });
    return x;
  };

  calculatePositions(ROOT_NODE_ID, 0);
  const rootX = positionedNodes.get(ROOT_NODE_ID)?.x || 0;
  const levelHeights = new Map<number, number>();

  nodes.forEach((node) => {
    const pos = positionedNodes.get(node.id);
    if (!pos) return;
    const level = pos.level;
    const height = (node.data as Node)?.height ?? NODE_HEIGHT;
    const current = levelHeights.get(level) ?? 0;
    levelHeights.set(level, Math.max(current, height));
  });

  const levelY = new Map<number, number>();
  levelY.set(0, 0);
  const maxLevel = Math.max(...Array.from(levelHeights.keys()));

  const getLevelGap = (level: number) => {
    if (level === 0) return 100;
    if (level === 1) return 140;
    return 180;
  };

  for (let level = 1; level <= maxLevel; level++) {
    const prevY = levelY.get(level - 1) ?? 0;
    const prevHeight = levelHeights.get(level - 1) ?? NODE_HEIGHT;
    levelY.set(level, prevY + prevHeight + getLevelGap(level - 1));
  }

  const layoutedNodes = nodes.map((node) => {
    const position = positionedNodes.get(node.id);
    const level = position?.level ?? 0;

    return {
      ...node,
      position: {
        x: (position?.x || 0) - rootX - NODE_WIDTH / 2,
        y: levelY.get(level) ?? 0,
      },
      sourcePosition: Position.Top,
      targetPosition: Position.Bottom,
    };
  });

  return [
    layoutedNodes,
    edges.map((edge) => ({
      ...edge,
      ...edgeStyle,
    })),
  ];
}

export const getCountry = (value?: string) => {
  if (value === '1') return 'РФ';
  if (value === '2') return 'РБ';
  return undefined;
};

export const renderIndividualStatus = (item: TForbidenCountryIndividual) => {
  const citizenValue = item.citizenshipStatus?.value;
  const residentValue = item.residencyStatus?.value;
  const countryCitizen = getCountry(citizenValue);
  const countryResident = getCountry(residentValue);

  const parts = [
    countryCitizen && `громадянин ${countryCitizen}`,
    countryResident && `резидент ${countryResident}`,
  ].filter(Boolean);

  return parts.join(' та ');
};

export const checkIndividual = (nodes: TOwnerDiagramNode[]): boolean => {
  return nodes.every((node) => {
    if (!individualTypes.includes(node.type)) return false;
    if (node.level === 2) return false;
    const children = (node as unknown as { children?: TOwnerDiagramNode[] }).children;
    if (children && children.length > 0) {
      return checkIndividual(children);
    }
    return true;
  });
};
